import { useEffect, useState, useCallback, ReactNode, useRef, useMemo } from 'react';
import { login as apiLogin, verify as apiVerify, refresh as apiRefresh, logout as apiLogout, check as apiCheck } from '../api';
import type { AuthContextType, Session, RefreshResponse } from '../types';
import { APIError, User } from '@promo/shared';
import { AuthContext } from './context';
import { getRefreshDelay } from '@promo/shared';
import { usePostHog } from 'posthog-js/react';
import { authService } from '../../../services/auth';

// Initial session state
const initialSession: Session = {
  user: null,
  isAuthenticated: false,
  isLoading: true,
  error: null,
  devToken: null,
  hasRefreshToken: false,
  email: null
};

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [session, setSession] = useState<Session>(initialSession);
  const refreshPromiseRef = useRef<Promise<RefreshResponse> | null>(null);
  const refreshTimerRef = useRef<number | null>(null);

  // Identify or reset PostHog when session user changes
  const posthog = usePostHog();
  useEffect(() => {
    if (session.user) {
      posthog.identify(session.user.id, {
        email: session.user.email,
        name: session.user.name || '',
      });
    } else {
      posthog.reset();
    }
  }, [session.user, posthog]);

  // Helper to update session with a user
  const setUser = useCallback((user: User | null, hasRefreshToken: boolean = false) => {
    setSession(prev => ({
      ...prev,
      user,
      isAuthenticated: !!user,
      isLoading: false,
      error: null,
      hasRefreshToken,
      email: user ? prev.email : null,
      devToken: user ? prev.devToken : null,
    }));
  }, []);

  // Setup automatic token refresh
  const setupRefreshTimer = useCallback((refreshFn: () => Promise<void>) => {
    // Clear any existing timer
    if (refreshTimerRef.current) {
      window.clearTimeout(refreshTimerRef.current);
      refreshTimerRef.current = null;
    }

    // Set new timer based on access token lifespan
    refreshTimerRef.current = window.setTimeout(refreshFn, getRefreshDelay());
  }, []);

  // Refresh session helper
  const refreshSession = useCallback(async (): Promise<void> => {
    // If there's already a refresh in progress, return that promise
    if (refreshPromiseRef.current) {
      await refreshPromiseRef.current;
      return;
    }

    setSession(prev => ({ ...prev, isLoading: true, error: null }));
    
    // Create new refresh promise
    refreshPromiseRef.current = (async () => {
      try {
        const response = await apiRefresh();
        const hasRefreshToken = !!(response.tokens && response.tokens.refreshToken);
        if (response.user) {
          setUser(response.user, hasRefreshToken);
          if (hasRefreshToken) {
            setupRefreshTimer(refreshSession);
          }
          return response;
        }
        // If we get here, something went wrong with the response format
        setUser(null, false);
        throw new Error('Invalid refresh response');
      } catch (error) {
        // Only clear user if it's not a network error or if the error is 401
        if (error instanceof APIError && error.status === 401) {
          setUser(null, false);
        }
        throw error;
      } finally {
        setSession(prev => ({ ...prev, isLoading: false }));
        refreshPromiseRef.current = null;
      }
    })() as Promise<RefreshResponse>;

    await refreshPromiseRef.current;
  }, [setUser, setupRefreshTimer]);

  // Check session on mount
  useEffect(() => {
    let mounted = true;

    const checkSession = async () => {
      try {
        // First try to verify the auth token
        const response = await apiCheck();
        if (mounted && response?.user) {
          setUser(response.user, response.hasRefreshToken);
          // If we have a refresh token, set up the refresh timer
          if (response.hasRefreshToken) {
            setupRefreshTimer(refreshSession);
          }
          return;
        }
      } catch (error) {
        // If check fails with 401 and we have a refresh token, try refreshing
        if (error instanceof APIError && 
            error.status === 401 && 
            error.details && 
            typeof error.details === 'object' &&
            'error' in error.details && // Check if 'error' property exists
            error.details.error && // Check if 'error.details.error' is not null/undefined
            typeof error.details.error === 'object' && // Check if 'error.details.error' is an object
            'details' in error.details.error && // Check if 'details' property exists in 'error.details.error'
            error.details.error.details && // Check if 'error.details.error.details' is not null/undefined
            typeof error.details.error.details === 'object' && // Check if 'error.details.error.details' is an object
            'hasRefreshToken' in error.details.error.details) { // Access hasRefreshToken correctly
          try {
            await refreshSession();
            // No need to check response since setUser is called inside refreshSession
          } catch (refreshError) {
            // Only clear user if it's not a network error or if the error is 401
            if (mounted && refreshError instanceof APIError && refreshError.status === 401) {
              setUser(null, false);
            }
          }
        } else {
          // If we don't have a refresh token, clear the user
          if (mounted) {
            setUser(null, false);
          }
        }
      } finally {
        if (mounted) {
          setSession(prev => ({ ...prev, isLoading: false }));
        }
      }
    };

    checkSession();

    return () => {
      mounted = false;
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
    };
  }, [refreshSession, setUser, setupRefreshTimer]);

  // Auth methods
  const login = useCallback(async (email: string) => {
    setSession(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await apiLogin(email);
      setSession(prev => ({
        ...prev,
        isLoading: false,
        error: null,
        devToken: response.token || null,  // Always set token if present
        email
      }));
      return response;
    } catch (error) {
      setSession(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to login',
        devToken: null,
        email: null
      }));
      throw error;
    }
  }, []);

  const verify = useCallback(async (token: string, rememberMe: boolean) => {
    setSession(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      const response = await apiVerify(token, rememberMe);
      setUser(response.user, response.hasRefreshToken);
      if (response.hasRefreshToken) {
        setupRefreshTimer(refreshSession);
      }
    } catch (error) {
      setSession(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to verify token',
      }));
      throw error;
    }
  }, [setUser, setupRefreshTimer, refreshSession]);

  const logout = useCallback(async () => {
    setSession(prev => ({ ...prev, isLoading: true, error: null }));
    try {
      await apiLogout();
      if (refreshTimerRef.current) {
        window.clearTimeout(refreshTimerRef.current);
        refreshTimerRef.current = null;
      }
      setUser(null, false);
    } catch (error) {
      setSession(prev => ({
        ...prev,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to logout',
      }));
      throw error;
    }
  }, [setUser]);

  const value: AuthContextType = useMemo(() => ({
    session,
    login,
    verify,
    logout,
    refreshSession,
  }), [session, login, verify, logout, refreshSession]);

  // Register auth context with global auth service
  useEffect(() => {
    authService.setAuthContext(value);
  }, [value]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
} 